import React, { useState } from "react";
import { Upload, Link2, Copy, Mail, CheckCircle2, XCircle } from "lucide-react";

const makeInviteLink = (courseId) => {
  const token = Math.random().toString(36).slice(2, 10);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://panel.local";
  return `${origin}/invite/${courseId}?t=${token}`;
};

export default function Invite({ courseId = "g-demo", groupTitle = "Dendrolog i Arborysta Bielsko-Biała" }) {
  const [emailsBulk, setEmailsBulk] = useState("");
  const [pending, setPending] = useState([]);
  const [inviteLink, setInviteLink] = useState(makeInviteLink(courseId));

  const parseEmails = (text) =>
    Array.from(
      new Set(
        text
          .split(/[\n,;\t ]+/)
          .map((s) => s.trim())
          .filter((s) => /.+@.+\..+/.test(s))
      )
    ).slice(0, 300);

  const addBulk = () => {
    const emails = parseEmails(emailsBulk);
    if (!emails.length) return alert("Wklej adresy e-mail (oddzielone enterem, przecinkiem lub spacją)");
    const toAdd = emails.map((e) => ({
      id: `${Date.now()}-${e}`,
      email: e,
      status: "invited",
    }));
    setPending((prev) => [...toAdd, ...prev]);
    setEmailsBulk("");
  };

  const triggerCSV = (setter) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,text/csv";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      const emails = text
        .split(/[\n,;\t ]+/)
        .map((s) => s.trim())
        .filter((s) => /.+@.+\..+/.test(s))
        .join("\n");
      setter(emails);
    };
    input.click();
  };

  const regenerateLink = () => {
    setInviteLink(makeInviteLink(courseId));
  };

  const styles = {
    container: { minHeight: "100vh", padding: "20px", fontFamily: "Arial, sans-serif", backgroundColor: "#f7f7f7" },
    card: { border: "1px solid #ccc", borderRadius: "16px", padding: "20px", backgroundColor: "#fff", marginBottom: "20px" },
    header: { marginBottom: "15px" },
    title: { fontSize: "1.5rem", fontWeight: "bold", marginBottom: "5px" },
    description: { fontSize: "0.9rem", color: "#555" },
    section: { border: "1px solid #ddd", borderRadius: "12px", padding: "15px", marginBottom: "20px" },
    button: { padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px", border: "1px solid #888", borderRadius: "6px", backgroundColor: "#eee" },
    buttonSecondary: { backgroundColor: "#ddd" },
    input: { padding: "8px", flex: 1, borderRadius: "6px", border: "1px solid #ccc" },
    textarea: { width: "100%", minHeight: "110px", padding: "8px", borderRadius: "6px", border: "1px solid #ccc" },
    table: { width: "100%", borderCollapse: "collapse", marginTop: "10px" },
    th: { borderBottom: "1px solid #ccc", textAlign: "left", padding: "8px" },
    td: { padding: "8px", borderBottom: "1px solid #eee" },
    statusInvited: { color: "#555", display: "inline-flex", alignItems: "center", gap: "5px" },
    statusJoined: { color: "green", display: "inline-flex", alignItems: "center", gap: "5px" },
    statusFailed: { color: "red", display: "inline-flex", alignItems: "center", gap: "5px" },
    footerText: { fontSize: "0.8rem", color: "#777" }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Dodaj kursantów – {groupTitle}</div>
          <div style={styles.description}>
            Dwie drogi zapraszania:<br />
            1) Masowe dodanie po e-mailu (20/30+ jednocześnie).<br />
            2) "Jednorożec" – pojedyncze zaproszenie z automatycznym utworzeniem konta.
          </div>
        </div>

        {/* Masowe dodawanie */}
        <div style={styles.section}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <div>
              <strong>Masowe dodawanie po e-mailu</strong>
              <div style={{ fontSize: "0.85rem", color: "#555" }}>
                Wklej listę e-maili (po enterach, przecinkach lub spacjach). System utworzy konta i wyśle zaproszenia.
              </div>
            </div>
            <button style={styles.button} onClick={() => triggerCSV(setEmailsBulk)}>
              <Upload size={16} /> Import CSV
            </button>
          </div>
          <textarea
            style={styles.textarea}
            value={emailsBulk}
            onChange={(e) => setEmailsBulk(e.target.value)}
            placeholder="jan.kowalski@mail.com\nanna.nowak@uczelnia.pl\n..."
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px" }}>
            <div style={styles.footerText}>Podpowiedź: błędne adresy zostaną odrzucone; limit jednorazowo ~300 adresów.</div>
            <button style={styles.button} onClick={addBulk}>
              <Mail size={16} /> Wyślij zaproszenia
            </button>
          </div>
        </div>

        {/* Jednorożec */}
        <div style={styles.section}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <div>
              <strong>Pojedyncze zaproszenie ("Jednorożec")</strong>
              <div style={{ fontSize: "0.85rem", color: "#555" }}>
                Skopiuj link dołączenia. Każdy link jest unikalny i przypisuje konto do tej destylowanej grupy/kursu.
              </div>
            </div>
            <button style={styles.button} onClick={regenerateLink}>
              <Link2 size={16} /> Wygeneruj nowy link
            </button>
          </div>
          <div style={{ display: "flex", gap: "5px" }}>
            <input style={styles.input} readOnly value={inviteLink} />
            <button style={{ ...styles.button, ...styles.buttonSecondary }} onClick={() => navigator.clipboard.writeText(inviteLink)}>
              <Copy size={16} /> Kopiuj
            </button>
          </div>
        </div>

        {/* Ostatnie zaproszenia */}
        <div style={styles.section}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
            <strong>Ostatnie zaproszenia</strong>
            <span style={{ fontSize: "0.85rem", color: "#777" }}>{pending.length} pozycji</span>
          </div>
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>E-mail</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {pending.length === 0 && (
                  <tr>
                    <td style={{ ...styles.td, textAlign: "center" }} colSpan={3}>Brak zaproszeń – dodaj adresy powyżej.</td>
                  </tr>
                )}
                {pending.map((s) => (
                  <tr key={s.id}>
                    <td style={styles.td}>{s.email}</td>
                    <td style={styles.td}>
                      {s.status === "invited" && <span style={styles.statusInvited}><Mail size={16} /> wysłane</span>}
                      {s.status === "joined" && <span style={styles.statusJoined}><CheckCircle2 size={16} /> dołączył(a)</span>}
                      {s.status === "failed" && <span style={styles.statusFailed}><XCircle size={16} /> błąd</span>}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <button style={styles.button} onClick={() => alert("Resend (demo)")}>Wyślij ponownie</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}